import Vue from "vue";
import { mapGetters } from "vuex";
import DiagramPartCard from "@/components/PartDiagramSearch/DiagramPartCard/DiagramPartCard.vue";
import { LoadingSpinner } from "@/components";
import { themeHelper } from "@/components/mixins/themeHelper";

/**
 * DiagramPartsList – Scrollable list of DiagramPartCard items shown alongside
 * the diagram viewer. Displays a heading with the part count and emits
 * alternative-part availability back to the parent.
 */
export default Vue.extend({
    name: "DiagramPartsList",
    components: {
        DiagramPartCard,
        LoadingSpinner,
    },
    mixins: [
        themeHelper
    ],
    props: {
        /** Whether the parts list is currently loading. */
        loading: {
            type: Boolean,
            default: false
        },
        /** Search results object containing a `Parts` array. */
        searchResults: {
            type: Object,
            default: () => ({})
        },
        /** Whether any returned parts have alternative part tabs. */
        hasAltParts: {
            type: Boolean,
            default: false
        },
    },
    data: () => ({
        /** Filter text for reference number search. */
        referenceFilter: "",
        /** Whether the suggestions dropdown is visible. */
        showSuggestions: false,
        /** Currently selected suggestion index for keyboard navigation. */
        selectedSuggestionIndex: -1
    }),
    computed: {
        /** True when the search results contain at least one part. */
        hasResults(): boolean {
            return (
                this.searchResults &&
                this.searchResults.Parts &&
                this.searchResults.Parts.length > 0
            );
        },
        /** Count of parts matching the current filter. */
        filteredCount(): number {
            if (!this.hasResults || !this.referenceFilter.trim()) {
                return this.searchResults.Parts?.length || 0;
            }
            const filter = this.referenceFilter.toLowerCase().trim();
            return this.searchResults.Parts.filter((part: any) => {
                const refNumber = part.ReferenceNumber || part.FullPartDetails?.ReferenceNumber || "";
                return refNumber.toLowerCase().startsWith(filter);
            }).length;
        },
        /** Unique reference numbers that match the current input for type-ahead. */
        suggestions(): string[] {
            if (!this.hasResults) {
                return [];
            }
            const filter = this.referenceFilter.toLowerCase().trim();
            const refNumbers = new Set<string>();
            
            this.searchResults.Parts.forEach((part: any) => {
                const refNumber = part.ReferenceNumber || part.FullPartDetails?.ReferenceNumber || "";
                if (refNumber) {
                    // If no filter, show all; otherwise filter by prefix
                    if (!filter || refNumber.toLowerCase().startsWith(filter)) {
                        refNumbers.add(refNumber);
                    }
                }
            });
            
            return Array.from(refNumbers)
                .sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));
        }
    },
    methods: {
        /** Bubble the hasAltParts event up to the parent component. */
        HasAltParts(emittedHasAlts: boolean) {
            this.$emit("hasAltParts", emittedHasAlts);
        },
        /** Check if a part matches the current reference filter. */
        partMatchesFilter(part: any): boolean {
            if (!this.referenceFilter.trim()) {
                return true;
            }
            const filter = this.referenceFilter.toLowerCase().trim();
            const refNumber = part.ReferenceNumber || part.FullPartDetails?.ReferenceNumber || "";
            return refNumber.toLowerCase().startsWith(filter);
        },
        /** Handle input focus - show suggestions. */
        onInputFocus() {
            if (this.suggestions.length > 0) {
                this.showSuggestions = true;
            }
        },
        /** Handle input change - show suggestions. */
        onInputChange() {
            this.selectedSuggestionIndex = -1;
            if (this.suggestions.length > 0) {
                this.showSuggestions = true;
            } else {
                this.showSuggestions = false;
            }
        },
        /** Select a suggestion from the dropdown. */
        selectSuggestion(refNumber: string) {
            this.referenceFilter = refNumber;
            this.showSuggestions = false;
            this.selectedSuggestionIndex = -1;
        },
        /** Handle keyboard navigation in suggestions. */
        handleKeydown(event: KeyboardEvent) {
            if (!this.showSuggestions || this.suggestions.length === 0) {
                return;
            }
            
            switch (event.key) {
                case "ArrowDown":
                    event.preventDefault();
                    this.selectedSuggestionIndex = Math.min(
                        this.selectedSuggestionIndex + 1,
                        this.suggestions.length - 1
                    );
                    break;
                case "ArrowUp":
                    event.preventDefault();
                    this.selectedSuggestionIndex = Math.max(this.selectedSuggestionIndex - 1, -1);
                    break;
                case "Enter":
                    event.preventDefault();
                    if (this.selectedSuggestionIndex >= 0) {
                        this.selectSuggestion(this.suggestions[this.selectedSuggestionIndex]);
                    }
                    break;
                case "Escape":
                    this.showSuggestions = false;
                    this.selectedSuggestionIndex = -1;
                    break;
            }
        },
        /** Close suggestions when clicking outside. */
        handleClickOutside(event: MouseEvent) {
            const target = event.target as HTMLElement;
            const searchContainer = this.$refs.searchContainer as HTMLElement;
            if (searchContainer && !searchContainer.contains(target)) {
                this.showSuggestions = false;
                this.selectedSuggestionIndex = -1;
            }
        }
    },
    mounted() {
        document.addEventListener("click", this.handleClickOutside);
    },
    beforeDestroy() {
        document.removeEventListener("click", this.handleClickOutside);
    }
});
